import { z } from 'zod';
import { RuleType } from '../../types';
import { ConfigRulesValidator } from '../config-rules.validator';
import { zodShards } from '../shards';
import { ValidateSegmentErrorType } from './validate-segment.types';
function validateObject(rules, fieldDef, visualRules, values, errors) {
    const segmentRulesForValidate = getRulesForSegment(rules, fieldDef);
    const segmentRulesForVisual = getRulesForSegment(visualRules, fieldDef);
    const { errors: structErrors } = validateSegment({
        values: values[fieldDef.name],
        def: fieldDef,
        rules: segmentRulesForValidate,
        visualRules: segmentRulesForVisual,
    });
    if (structErrors.length) {
        errors.push(...structErrors.map(({ message, path, type }) => ({
            type,
            message,
            path: [`${fieldDef.name}.${path}`],
        })));
    }
}
function validateArray(values, fieldDef, rules, visualRules, errors) {
    if (isRequired(rules, fieldDef, RuleType.SEGMENT) && !values[fieldDef.name].length) {
        errors.push({
            path: [fieldDef.name],
            message: 'Required segment',
            type: ValidateSegmentErrorType.SEGMENT,
        });
    }
    values[fieldDef.name].forEach((value, index) => {
        const segmentRulesForValidate = getRulesForArraySegment(rules, fieldDef, index);
        const segmentRulesForVisual = getRulesForArraySegment(visualRules, fieldDef, index);
        const { errors: structErrors } = validateSegment({
            values: value,
            def: fieldDef,
            rules: segmentRulesForValidate,
            visualRules: segmentRulesForVisual,
        });
        if (structErrors.length) {
            errors.push(...structErrors.map(({ message, path, type }) => ({
                type,
                message,
                path: [`${fieldDef.name}.${index}.${path}`],
            })));
        }
    });
}
const validateDocument = ({ def, rules, values, visualRules, errors, }) => {
    if (visualRules.find((item) => item.rule === 'hide' && item.path === def.name)) {
        return;
    }
    // If we don't see the documents' uploader component we don't check the required of this one
    if (isDocumentUploaderComponentVisible({ visualRules, name: def.name }) &&
        isRequired(rules, def, RuleType.DOCUMENT)) {
        if (def.isMultiple) {
            if (values[def.name].length) {
                values[def.name].forEach((item, index) => {
                    if (!item.files?.length && !item.reason) {
                        errors.push({
                            message: messages.required,
                            path: [`${def.name}.${index}.reason`],
                        });
                    }
                });
            }
        }
        else {
            if (!(values[def.name].files && values[def.name].files.length) && !values[def.name].reason) {
                errors.push({
                    message: messages.required,
                    path: [`${def.name}.reason`],
                });
            }
        }
    }
    if (def.isMultiple) {
        validateArray(values, def, rules, visualRules, errors);
    }
    else {
        validateObject(rules, def, visualRules, values, errors);
    }
};
function refineConfigurationRules(segmentShape, rules, def, visualRules) {
    return z.object(segmentShape).superRefine((values, ctx) => {
        const validator = new ConfigRulesValidator({ values, ctx, rules });
        if (!def.fields)
            return;
        for (const field of Object.values(def.fields)) {
            const fieldRules = rules.filter((rule) => rule.path === field.name);
            const fieldVisualRules = visualRules.filter((rule) => {
                return rule.path === field.name;
            });
            if (!isFieldVisible(fieldVisualRules)) {
                continue;
            }
            if (field.type === 'phone') {
                validator.phone(field.name);
            }
            if (field.type === 'email') {
                validator.email(field.name);
            }
            if (fieldRules.length === 0) {
                continue;
            }
            // * Required fields validation
            validator.required(field.name);
            // * Min/max dates validation
            if (field.type === 'date') {
                validator.minRelativeDate(field.name);
                validator.maxRelativeDate(field.name);
            }
        }
    });
}
function isPossibleParsingStruct(fieldDef, values) {
    return fieldDef.kind === 'struct' && values[fieldDef.name];
}
function isSegmentFilled(values, fieldDef) {
    return Object.hasOwn(values, fieldDef.name);
}
function isSegmentVisible(visualRules, fieldDef) {
    return !visualRules.find((item) => fieldDef.name === item.path && item.rule === 'hide');
}
function isRequired(rules, fieldDef, type = null) {
    return (rules.findIndex((item) => fieldDef.name === item.path && item.rule === 'required' && (type ? item.type === type : true)) >= 0);
}
function isDocumentUploaderComponentVisible({ visualRules, name }) {
    return (visualRules.findIndex((item) => item.type === RuleType.DOCUMENT && item.rule === 'hide' && item.path === name) < 0);
}
/**
 * Validation function for a single segment
 * @param opts ValidateSegmentOptions
 * @returns ValidateSegmentResult
 */
export const validateSegment = ({ values, def, rules, visualRules, }) => {
    // Our def could have an empty data or could be there by a mistake
    if (!def.fields || !values) {
        return { errors: [], isValid: true };
    }
    const errors = [];
    // * Create a segment shape
    const segmentShape = {};
    // * Apply type rules
    for (const fieldDef of Object.values(def.fields)) {
        // We could have a field in def, but not in values
        if (isSegmentFilled(values, fieldDef) && isSegmentVisible(visualRules, fieldDef)) {
            segmentShape[fieldDef.name] = getBaseRule(fieldDef);
            if (isPossibleParsingStruct(fieldDef, values)) {
                if (fieldDef.fieldKind === 'document') {
                    validateDocument({ def: fieldDef, rules, visualRules, errors, values });
                }
                else {
                    if (fieldDef.isMultiple) {
                        validateArray(values, fieldDef, rules, visualRules, errors);
                    }
                    else {
                        validateObject(rules, fieldDef, visualRules, values, errors);
                    }
                }
            }
        }
    }
    const scheme = refineConfigurationRules(segmentShape, rules, def, visualRules);
    const result = scheme.safeParse(values);
    const isValid = result.success;
    if (!isValid) {
        errors.push(...result.error.issues.map((value) => ({
            message: value.message,
            path: value.path.filter((p) => typeof p !== 'symbol'),
        })));
    }
    return { isValid: !errors.length, errors };
};
const startsWithNumber = (str) => {
    return /^\d/.test(str);
};
const messages = {
    required: 'Required field',
};
const getRulesForSegment = (rules, fieldDef) => {
    return rules
        .filter(({ path }) => path.startsWith(`${fieldDef.name}.`))
        .map(({ path, ...rest }) => ({ path: path.replace(`${fieldDef.name}.`, ''), ...rest }));
};
const getRulesForArraySegment = (rules, fieldDef, index) => {
    const segmentRules = getRulesForSegment(rules, fieldDef);
    // For rules look like fieldname.0.fieldname
    return [
        ...segmentRules
            .filter(({ path }) => path.startsWith(`${index}.`))
            .map(({ path, ...rest }) => ({ path: path.replace(`${index}.`, ''), ...rest })),
        ...segmentRules.filter(({ path }) => !startsWithNumber(path)),
    ];
};
const isFieldVisible = (rules) => {
    return !rules.some((rule) => rule.rule === 'hide');
};
const getBaseRule = (field) => {
    if (field.type === 'struct') {
        // * Create a struct shape
        const structShape = {};
        // * Apply field rules to shape. Iterate the child definitions, not the
        // definition object's own properties — the latter builds a phantom
        // "undefined" shape key, which zod 4 rejects as nonoptional.
        for (const fieldDef of Object.values(field.fields ?? {})) {
            structShape[fieldDef.name] = getBaseRule(fieldDef);
        }
        // * Wrap shape in array if it's a list. Optional like every other
        // branch: presence is enforced by the rules validator, not the shape.
        if (field.isMultiple) {
            return z.array(z.object(structShape)).optional();
        }
        return z.object(structShape).optional();
    }
    if (field.type === 'text') {
        return z
            .string({ error: messages.required })
            .transform((value) => value.trim())
            .optional();
    }
    if (field.type === 'number') {
        return z.number().optional();
    }
    if (field.type === 'date') {
        return zodShards.date.optional();
    }
    if (field.type === 'boolean') {
        return z.boolean().optional();
    }
    if (field.type === 'enum') {
        const enumRule = z.any();
        return enumRule.optional();
    }
    return z.any();
};
//# sourceMappingURL=validate-segment.js.map